import {
  DEFAULT_INPUT_HEIGHT,
  FormRow,
  Input,
  Skeleton,
  Spacer,
  Stack,
  Wizard,
  useWizardPage,
  Link,
  Paragraph,
  Title
} from '@efast_public/fjd-component-library';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ClientInformationDto } from '../../../../api/hooks/useFitConnectClientInformation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BehApiRoutes, behClient } from '../../../../api/beh-api';
import { AxiosError } from 'axios';
import Joi from 'joi';
import { joiResolver } from '@hookform/resolvers/joi';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNotification } from '../../../../shared/notification/useNotification';
import { ErrorType } from '../../../../shared/utils/Error/ErrorCode';
import {
  FITCONNECT_SSP_SUBSCRIBER_CLIENTS,
  FJD_DOCS_EINRICHTUNGSASSISTANT,
  FJD_DOCS_SETTINGS_FITCONNECT
} from '../../../../shared/utils/Links';

export function FitConnect({ isSettings }: { isSettings?: boolean }) {
  const queryClient = useQueryClient();

  const { notifyUserFromRequest, notifyUser } = useNotification();
  const { t } = useTranslation();

  const {
    data: fitConnectClientInformationInDB,
    isLoading: isLoadingFitConnectClientInformationGet
  } = useQuery<ClientInformationDto, AxiosError>({
    queryKey: [BehApiRoutes.fitConnectClientInformation()]
  });

  const [hasSubmissionError, setHasSubmissionError] = useState(false);

  const {
    formState: { isValid: formInputIsValid, isDirty },
    register,
    handleSubmit,
    reset
  } = useForm<ClientInformationDto>({
    mode: 'onChange',
    defaultValues: {
      clientId: '',
      clientSecret: ''
    },
    resolver: joiResolver(
      Joi.object<ClientInformationDto>({
        clientId: Joi.string().guid().required(),
        clientSecret: Joi.string().required()
      })
    )
  });

  const {
    mutateAsync: putFitConnectClientInformationInDB,
    isPending: isLoadingPutFitConnectClientInformation
  } = useMutation({
    mutationKey: [BehApiRoutes.fitConnectClientInformation()],
    mutationFn: (clientInformationDto: ClientInformationDto) =>
      notifyUserFromRequest(
        () =>
          behClient.put(
            BehApiRoutes.fitConnectClientInformation(),
            clientInformationDto
          ),
        {
          successMessage: t(
            'settings.destinationSync.clientSync.fitCoConnectSuccess'
          ),
          errorMessage: t(
            'settings.destinationSync.clientSync.fitCoConnectError'
          ),
          errorIdHandler: {
            'behoerdenclient/problems/clientInvalid': () => {
              notifyUser({
                intent: 'error',
                title: t('api.error.createFitConnectSenderCreds.invalidCreds')
              });
            },
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_ILLEGAL_CREDENTIAL_NOT_A_SUBSCRIBER]:
              () => {
                notifyUser({
                  intent: 'error',
                  title: t(
                    'api.error.createFitConnectSubscriberCreds.notASubscriber'
                  )
                });
              }
          }
        }
      ),
    onSuccess: () => {
      setHasSubmissionError(false);
      queryClient.invalidateQueries({
        queryKey: [BehApiRoutes.fitConnectClientInformation()],
        exact: true
      });
    },
    onError: () => {
      setHasSubmissionError(true);
    }
  });

  const onSubmit = useCallback(
    () =>
      handleSubmit(async (data) => putFitConnectClientInformationInDB(data))(),
    [handleSubmit, putFitConnectClientInformationInDB]
  );

  useEffect(() => {
    if (isDirty && hasSubmissionError) {
      setHasSubmissionError(false);
    }
  }, [isDirty, hasSubmissionError]);

  const { wizardState } = useWizardPage({
    isFormValid: formInputIsValid && !hasSubmissionError,
    headline: (state) =>
      `Schritt ${state.currentPageIndex + 1} von ${state.pagesLength}`,
    onSubmit,
    primaryButtonLabel: t(
      'settings.destinationSync.clientSync.fitCoConnectButton'
    )
  });

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (
      fitConnectClientInformationInDB &&
      !hasInitialized.current &&
      !isDirty
    ) {
      reset(
        {
          clientId: fitConnectClientInformationInDB.clientId,
          clientSecret: fitConnectClientInformationInDB.clientSecret
        },
        {
          keepDirty: true
        }
      );
      hasInitialized.current = true;
    }
  }, [fitConnectClientInformationInDB, reset, isDirty]);

  return (
    <Wizard.Page>
      <Stack spacing="m">
        <Title level={2} visualLevel={6}>
          {t('configAssistent.fitConnect.headline')}
        </Title>
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Paragraph>
            <Trans
              i18nKey="configAssistent.fitConnect.descriptionText"
              components={{
                Link: (
                  <Link
                    href={FITCONNECT_SSP_SUBSCRIBER_CLIENTS}
                    target="_blank"
                  >
                    link
                  </Link>
                )
              }}
            />
            <Trans
              i18nKey="configAssistent.configAssistentDocumentationNotice"
              components={{
                Link: (
                  <Link
                    href={
                      isSettings
                        ? FJD_DOCS_SETTINGS_FITCONNECT
                        : FJD_DOCS_EINRICHTUNGSASSISTANT
                    }
                    target="_blank"
                  >
                    hier
                  </Link>
                )
              }}
            />
          </Paragraph>
          <Spacer size="s" />
          <Stack spacing="m" direction="column">
            <FormRow label={t('settings.destinationSync.clientSync.idText')}>
              {isLoadingFitConnectClientInformationGet ? (
                <Skeleton
                  key={'loadingDestinationSyncClientSyncClientId'}
                  height={DEFAULT_INPUT_HEIGHT}
                />
              ) : (
                <Input
                  {...register('clientId')}
                  data-testid="destinationSyncClientSyncClientId"
                  id="destinationSyncClientSyncClientId"
                  type="text"
                  placeholder={t(
                    'settings.destinationSync.clientSync.idPlaceholder'
                  )}
                  disabled={isLoadingPutFitConnectClientInformation}
                />
              )}
            </FormRow>
            <FormRow
              label={t('settings.destinationSync.clientSync.secretText')}
            >
              {isLoadingFitConnectClientInformationGet ? (
                <Skeleton
                  key={'loadingDestinationSyncClientSyncClientSecret'}
                  height={DEFAULT_INPUT_HEIGHT}
                />
              ) : (
                <Input
                  {...register('clientSecret')}
                  enablePasswordToggle
                  placeholder={t(
                    'settings.destinationSync.clientSync.secretPlaceholder'
                  )}
                  data-testid="destinationSyncClientSyncClientSecret"
                  id="destinationSyncClientSyncClientSecret"
                  type="password"
                  disabled={isLoadingPutFitConnectClientInformation}
                />
              )}
            </FormRow>
          </Stack>
        </form>
      </Stack>
    </Wizard.Page>
  );
}
